import { useFormContext } from 'react-hook-form';
// Helpers
import { calculateAffiliateCommission } from 'helpers/affiliate';
import { formatPriceToUSD } from 'helpers/priceText';
// Ui
import Text from 'ui3/Text/Text';
import Switch from 'ui3/Switch/Switch';
import Input from 'ui3/Input/Input';
// Styles
import styles from './AffiliateProgramSection.module.scss';

export type AffiliateFormFields = {
  affiliateEnabled: boolean;
  affiliateCommissionPercent: string;
};

type AffiliateProgramSectionProps = {
  /** Organizer's share of one sale after the platform fee; the commission base. */
  netProfit: number;
  /** Free streams have nothing to share, so the program cannot be turned on. */
  disabled?: boolean;
};

const AffiliateProgramSection = ({
  netProfit,
  disabled = false,
}: AffiliateProgramSectionProps): JSX.Element => {
  const {
    register,
    watch,
    errors,
    trigger,
  } = useFormContext<AffiliateFormFields>();

  const isEnabled = Boolean(watch('affiliateEnabled')) && !disabled;
  const commissionPercent = Number(watch('affiliateCommissionPercent'));
  const participantEarns = calculateAffiliateCommission(
    netProfit,
    commissionPercent
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.copy}>
          <Text variant="body1Medium16">Affiliate Program</Text>
          <Text variant="body2Regular" color="lights-low">
            Offer Participants a percentage of each PPV sale they generate
            through their unique affiliate link. One commission rate applies to
            every Participant in this event.
          </Text>
        </div>
        <Switch
          wrapperClassName={styles.switch}
          name="affiliateEnabled"
          ref={register}
          disabled={disabled}
        />
      </div>

      {isEnabled && (
        <div className={styles.commissionField}>
          <Input
            name="affiliateCommissionPercent"
            label="Affiliate Commission"
            aria-label="Affiliate Commission"
            placeholder="10"
            inputMode="decimal"
            ref={register}
            onChange={() => trigger('affiliateCommissionPercent')}
            error={errors?.affiliateCommissionPercent?.message}
            icon="percent"
          />
          <Text as="span" variant="captionRegular" color="lights-low">
            Participant earns{' '}
            <Text as="span" variant="captionRegular" color="accent-default">
              {formatPriceToUSD(participantEarns)}
            </Text>{' '}
            per PPV sale.
          </Text>
        </div>
      )}
    </div>
  );
};

export default AffiliateProgramSection;
